import { FunctionComponent, ReactNode, useState } from "react";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { AnchoredPopover } from "../../Contacts/components/LeadPanelActions/AnchoredPopover";

export interface FilterPillProps {
	icon: IconDefinition;
	/** The muted prefix, e.g. "List". */
	label: string;
	/** The current selection, e.g. "All" or "3 selected". */
	value: string;
	/** Popover body. Receives a closer so a row can dismiss the menu after acting. */
	children: (close: () => void) => ReactNode;
	popoverWidth?: number;
	testId?: string;
	disabled?: boolean;
}

/**
 * One control in the Analytics filter bar: a compact pill showing `Label: value`, opening
 * a popover.
 *
 * The trigger deliberately mirrors the shared `Dropdown`'s `pill` variant class-for-class,
 * so these sit beside a real Dropdown without looking like a different control. The menu
 * itself cannot use `Dropdown` — that is single-select, and every filter here is a
 * multi-select — so it reuses `AnchoredPopover`, which already portals with
 * `position: fixed` and resolves outside clicks through `composedPath()` (web rule §11).
 */
export const FilterPill: FunctionComponent<FilterPillProps> = ({
	icon,
	label,
	value,
	children,
	popoverWidth = 288,
	testId,
	disabled = false,
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	return (
		<>
			<button
				className={classNames(
					"inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-lg border border-border-light bg-brand-white px-2 py-[5px] text-xs font-medium text-text-meta duration-200",
					disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-background-light"
				)}
				data-testid={testId}
				disabled={disabled}
				type="button"
				onClick={(event) => {
					// Capture before the updater runs — `currentTarget` is nulled after dispatch.
					const trigger = event.currentTarget as HTMLElement;

					setAnchorEl((current) => (current ? null : trigger));
				}}
			>
				<Icon className="w-3 h-3 text-text-disabled" icon={icon} />
				<span className="text-text-disabled">{label}:</span>
				<span className="text-text">{value}</span>
				<Icon className="w-2.5 h-2.5 text-text-disabled" icon={faChevronDown} />
			</button>

			{anchorEl ? (
				<AnchoredPopover
					align="left"
					anchorEl={anchorEl}
					width={popoverWidth}
					onClose={() => setAnchorEl(null)}
				>
					{/*
					 * `AnchoredPopover` positions but does not paint — it renders a bare `fixed`
					 * wrapper and leaves the card to its content. Without an opaque panel the menu
					 * is see-through: whatever sits under it (an empty-state card, table rows)
					 * reads straight through the options, which looks exactly like a z-index bug.
					 */}
					<div className="rounded-lg border border-border-light bg-brand-white p-1 shadow-lg">
						{children(() => setAnchorEl(null))}
					</div>
				</AnchoredPopover>
			) : null}
		</>
	);
};

export interface FilterPillOption {
	id: number | string;
	title: string;
	subtitle?: string;
}

export interface FilterPillCheckListProps {
	options: FilterPillOption[];
	selectedIds: (number | string)[];
	onToggle: (id: number | string) => void;
	onSelectAll: () => void;
	allLabel: string;
	emptyLabel: string;
	/** Rendered above the "all" reset — the List filter puts its quick filters here. */
	header?: ReactNode;
	/** Enables the search box. Omit for a filter whose options all fit on screen. */
	onSearch?: (value: string) => void;
	searchPlaceholder?: string;
	/** Enables the "load more" row for a paginated option set. */
	onLoadMore?: () => void;
	loadMoreLabel?: string;
	hasMore?: boolean;
	isFetchingMore?: boolean;
}

/**
 * The body shared by every multi-select filter: an "all" reset followed by checkable rows,
 * optionally searchable and paginated.
 *
 * "All" is a reset rather than a select-everything toggle — an empty selection is what the
 * API reads as "no filter", and enumerating every id would silently break as soon as a new
 * list or user appears.
 */
export const FilterPillCheckList: FunctionComponent<FilterPillCheckListProps> = ({
	options,
	selectedIds,
	onToggle,
	onSelectAll,
	allLabel,
	emptyLabel,
	header,
	onSearch,
	searchPlaceholder,
	onLoadMore,
	loadMoreLabel,
	hasMore = false,
	isFetchingMore = false,
}) => (
	<div className="flex flex-col max-h-[320px] overflow-y-auto">
		{onSearch ? (
			<input
				className="mb-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-text duration-200 hover:border-brand-light focus:border-brand-light focus:outline-none"
				data-testid="filter-search"
				placeholder={searchPlaceholder}
				type="text"
				onChange={(event) => onSearch(event.target.value)}
			/>
		) : null}

		{header}

		<button
			className={classNames(
				"flex flex-row items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs font-semibold cursor-pointer hover:bg-background-light",
				selectedIds.length === 0 ? "text-brand-light" : "text-text-meta"
			)}
			type="button"
			onClick={onSelectAll}
		>
			{allLabel}
		</button>

		{options.length === 0 ? (
			<span className="px-2 py-1.5 text-xs text-text-disabled">{emptyLabel}</span>
		) : null}

		{options.map((option) => {
			const isSelected = selectedIds.includes(option.id);

			return (
				<button
					key={option.id}
					className="flex flex-row items-start gap-2 rounded-lg px-2 py-1.5 text-left cursor-pointer hover:bg-background-light"
					data-testid={`filter-option-${option.id}`}
					type="button"
					onClick={() => onToggle(option.id)}
				>
					<span
						className={classNames(
							"mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border text-[9px] font-bold",
							isSelected
								? "border-brand-light bg-brand-light text-brand-white"
								: "border-border-light bg-brand-white"
						)}
					>
						{isSelected ? "✓" : ""}
					</span>
					<span className="min-w-0 flex-1">
						<span className="block truncate text-xs text-text">{option.title}</span>
						{option.subtitle ? (
							<span className="block truncate text-[10px] text-text-meta">{option.subtitle}</span>
						) : null}
					</span>
				</button>
			);
		})}

		{onLoadMore && hasMore ? (
			<button
				className="rounded-lg px-2 py-1.5 text-left text-xs font-semibold text-brand-light cursor-pointer hover:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
				data-testid="filter-load-more"
				disabled={isFetchingMore}
				type="button"
				onClick={onLoadMore}
			>
				{loadMoreLabel}
			</button>
		) : null}
	</div>
);
